/**
 * ModuleLauncher — ultra-designer tile-based module picker.
 *
 * Landing screen shown right after a user selects their active projects.
 * No sidebar. Tiles are grouped by category and animate with framer-motion.
 * Clicking a tile morphs it into the top-left corner (where the sidebar lives)
 * then routes to the module — the DashboardLayout renders the sidebar on the
 * destination page, so the effect reads as a shared-layout morph.
 *
 * The current sidebar is always one click away: DashboardLayout has a
 * "Modules" grid button + Panorix-logo click → both return here.
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, FolderKanban, Layers, Shield, AlertTriangle, Camera,
  Wrench, HardHat, Boxes, FileText, ScanLine, FileDown, Bell, FileSearch,
  Sparkles, Crown, Users, Building2, Settings as SettingsIcon, LogOut, FileCheck2,
  Search, ChevronsUpDown, Check, Grid3x3, Plug,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAllowedModules } from '@/hooks/useAllowedModules';
import { useProject } from '@/contexts/ProjectContext';
import { useTheme } from '@/contexts/ThemeContext';
import { PanorixLogoStacked } from '@/components/PanorixLogo';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList, CommandEmpty } from '@/components/ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

// ── Tile catalogue — mirrors the sidebar screenshot ────────────────────────
const GROUPS = [
  {
    id: 'overview', title: 'Overview',
    subtitle: 'Portfolio-wide command posts',
    tiles: [
      { path: '/portfolio',        label: 'Portfolio',        icon: LayoutDashboard, desc: 'Executive KPI cockpit', tone: 'a' },
      { path: '/projects',         label: 'Projects',         icon: FolderKanban,    desc: 'Every active project',  tone: 'b' },
      { path: '/project-controls', label: 'Project Controls', icon: Layers,          desc: 'Cost · Time · Scope',   tone: 'c' },
    ],
  },
  {
    id: 'risk', title: 'Safety & Risk',
    subtitle: 'Guarding the frontline',
    tiles: [
      { path: '/project-controls/hse',             label: 'HSE Management', icon: Shield,         desc: 'Incidents · Audits',    tone: 'd' },
      { path: '/project-controls/raid',            label: 'RAID Log',       icon: AlertTriangle,  desc: 'Risks · Actions · Issues', tone: 'a' },
      { path: '/project-controls/site-monitoring', label: 'Site Monitoring',icon: Camera,         desc: 'Live camera & IOT',     tone: 'b' },
    ],
  },
  {
    id: 'field', title: 'Field Execution',
    subtitle: 'Where steel meets ground',
    tiles: [
      { path: '/piping-workfront',                   label: 'Piping Workfront',      icon: Wrench,  desc: 'Isometrics · Spools',    tone: 'c' },
      { path: '/structural-workfront',               label: 'Structural Workfront',  icon: HardHat, desc: 'Marks · Fabrication',    tone: 'd' },
      { path: '/project-controls/material-tracking', label: 'Material Tracking',     icon: Boxes,   desc: 'Inventory · Movement',   tone: 'a' },
    ],
  },
  {
    id: 'commercial', title: 'Commercial',
    subtitle: 'Contracts, claims, cashflow',
    tiles: [
      { path: '/contracts', label: 'Contracts & Claims', icon: FileText,   desc: 'Obligations · LDs · Variations', tone: 'b' },
      { path: '/tenders',   label: 'Tenders',            icon: FileSearch, desc: 'Prediction & scenarios',          tone: 'c' },
      { path: '/project-controls/nfa', label: 'NFA Management', icon: FileCheck2, desc: 'Notes for Approval · SAP BTP', tone: 'd' },
    ],
  },
  {
    id: 'insights', title: 'Insights & Documents',
    subtitle: 'Reporting, docs, AI',
    tiles: [
      { path: '/project-controls/scan-to-model',    label: 'Scan-to-Model',      icon: ScanLine, desc: 'Deviation & digital twin', tone: 'd' },
      { path: '/project-controls/progress-reports', label: 'Progress Reports',   icon: FileDown, desc: 'DPR · WPR · MPR',          tone: 'a' },
      { path: '/settings/notifications',            label: 'Notifications',      icon: Bell,     desc: 'Time-bar & alerts',        tone: 'b' },
      { path: '/project-controls/dms',              label: 'Document Management',icon: FileText, desc: 'Documents · Revisions',   tone: 'c' },
      { path: '/ai-analyst',                        label: 'AI Analyst',         icon: Sparkles, desc: 'Cognara-powered insights', tone: 'd' },
      { path: '/ceo-briefing',                      label: 'CEO Briefing',       icon: Crown,    desc: 'The 60-second view',       tone: 'a' },
    ],
  },
];

const ADMIN_GROUP = {
  id: 'admin', title: 'Administration',
  subtitle: 'Access, org, preferences',
  tiles: [
    { path: '/users',    label: 'Users',           icon: Users,       desc: 'Access & seats',       tone: 'b', adminOnly: true },
    { path: '/org',      label: 'My Organization', icon: Building2,   desc: 'Company & members',    tone: 'c', adminOnly: true },
    { path: '/settings', label: 'Settings',        icon: SettingsIcon,desc: 'Preferences & theme',  tone: 'd' },
    { path: '/settings/connectors', label: 'Connectors', icon: Plug, desc: 'P6 · MS Project · SAP · Oracle', tone: 'a', adminOnly: true },
  ],
};

// Gradient tokens per tone — light background, distinct accents.
const TONE_MAP = {
  a: { grad: 'linear-gradient(135deg,#FFEFE4 0%,#FFFFFF 55%,#FFE1CB 100%)', accent: '#F26B1F', dot: '#F26B1F' },
  b: { grad: 'linear-gradient(135deg,#E4EEFA 0%,#FFFFFF 55%,#CFDDF3 100%)', accent: '#1E4E8C', dot: '#1E4E8C' },
  c: { grad: 'linear-gradient(135deg,#E9F6EF 0%,#FFFFFF 55%,#CFEBDB 100%)', accent: '#0E7C6B', dot: '#0E7C6B' },
  d: { grad: 'linear-gradient(135deg,#F3EAFB 0%,#FFFFFF 55%,#E1CDF5 100%)', accent: '#7B3FA1', dot: '#7B3FA1' },
};

export default function ModuleLauncher() {
  const navigate = useNavigate();
  const { user, logout, isAdmin, permissions } = useAuth();
  const { projects, selectedProject, selectProject, loading: projectsLoading } = useProject();
  const { isDark } = useTheme();
  const [flying, setFlying] = useState(null); // { path, rect }
  const [q, setQ] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const isTrial = user?.platformRole === 'TRIAL_USER';
  const { paths: allowedPaths, isFullAccess } = useAllowedModules();
  const showAdmin = !isTrial && (isAdmin?.() || permissions?.role === 'admin');
  const T = isDark
    ? { title: '#F8FAFC', body: '#CBD5E1', chipBg: 'rgba(255,255,255,0.06)', chipBorder: 'rgba(255,255,255,0.12)', chipText: '#F8FAFC', tileTitle: '#0B1F3A', tileDesc: '#334155' }
    : { title: '#0B1F3A', body: '#334155', chipBg: 'rgba(255,255,255,0.85)', chipBorder: 'rgba(15,23,42,0.08)', chipText: '#0F172A', tileTitle: '#0B1F3A', tileDesc: '#475569' };

  const groups = useMemo(() => {
    // Tiles are filtered by what the selected project actually grants: the
    // catalogue is static, so without this a user restricted to one module
    // still saw a tile for every module on the launcher.
    const permitted = (tiles) => (isFullAccess || !allowedPaths)
      ? tiles
      : tiles.filter(t => t.adminOnly || allowedPaths.has(t.path) || t.path === '/settings');
    const base = GROUPS
      .map(g => ({ ...g, tiles: permitted(g.tiles) }))
      .filter(g => g.tiles.length);
    if (showAdmin) base.push({
      ...ADMIN_GROUP,
      tiles: ADMIN_GROUP.tiles.filter(t => !(isTrial && t.path === '/settings')),
    });
    if (!q.trim()) return base;
    const needle = q.trim().toLowerCase();
    return base
      .map(g => ({ ...g, tiles: g.tiles.filter(t =>
        t.label.toLowerCase().includes(needle) || t.desc.toLowerCase().includes(needle)
      ) }))
      .filter(g => g.tiles.length);
  }, [q, showAdmin, isTrial, isFullAccess, allowedPaths]);

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';

  const handleTileClick = (tile, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFlying({ path: tile.path, rect, tone: tile.tone, label: tile.label, Icon: tile.icon });
    // Give the morph 380ms to play, then land on the module.
    setTimeout(() => navigate(tile.path), 380);
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  return (
    <div className="min-h-screen relative overflow-hidden"
      style={{ background: isDark
        ? 'radial-gradient(1200px 700px at 15% -10%, rgba(30,78,140,0.28), transparent 60%), radial-gradient(900px 600px at 90% 10%, rgba(242,107,31,0.16), transparent 60%), #060A12'
        : 'radial-gradient(1200px 700px at 15% -10%, rgba(30,78,140,0.10), transparent 60%), radial-gradient(900px 600px at 90% 10%, rgba(242,107,31,0.10), transparent 60%), #F6F8FC'
      }}
      data-testid="module-launcher"
    >
      {/* Grain overlay */}
      <div className="pointer-events-none fixed inset-0 opacity-[0.035] mix-blend-multiply"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />

      {/* Header — no sidebar, launcher chrome */}
      <header className="relative z-10 flex items-center justify-between px-8 py-5">
        <div className="flex items-center gap-6">
          <div className="origin-left"><PanorixLogoStacked logomarkSize={200} /></div>
          {/* Project selector — carried over so users can switch context here */}
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <button data-testid="launcher-project-selector"
                className="flex items-center gap-2 px-3.5 py-2 rounded-full transition-all hover:shadow-md"
                style={{ background: T.chipBg, border: `1px solid ${T.chipBorder}`, backdropFilter: 'blur(8px)' }}>
                <FolderKanban className="w-3.5 h-3.5" style={{ color: '#F26B1F' }} />
                <span className="text-xs font-semibold max-w-[220px] truncate" style={{ color: T.chipText }}>
                  {projectsLoading ? 'Loading…' : selectedProject?.name || 'Select project'}
                </span>
                <ChevronsUpDown className="w-3 h-3 opacity-60" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <Command>
                <CommandInput placeholder="Search projects…" className="text-xs" />
                <CommandList>
                  <CommandEmpty className="py-4 text-center text-xs">No projects found</CommandEmpty>
                  <CommandGroup heading="Projects">
                    {projects.map((p) => (
                      <CommandItem key={p.project_id} value={p.name}
                        onSelect={() => { selectProject(p.project_id); setPickerOpen(false); }}
                        className="flex items-center gap-2 text-xs cursor-pointer"
                        data-testid={`launcher-project-option-${p.project_id}`}>
                        <Check className={`w-3 h-3 ${selectedProject?.project_id === p.project_id ? 'opacity-100' : 'opacity-0'}`} style={{ color: '#F26B1F' }} />
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium">{p.name}</div>
                          <div className="truncate text-[10px] opacity-60">{p.project_id}</div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative hidden md:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-60" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Filter modules…"
              data-testid="launcher-search"
              className="rounded-full pl-9 pr-4 py-2 text-xs w-60 focus:outline-none focus:ring-2 focus:ring-[#F26B1F]/40 transition-all"
              style={{ background: T.chipBg, border: `1px solid ${T.chipBorder}`, backdropFilter: 'blur(8px)', color: T.chipText }}
            />
          </div>
          <button onClick={() => navigate('/project-selector')} data-testid="launcher-change-projects"
            className="text-xs px-3 py-2 rounded-full font-medium transition-all hover:shadow-md"
            style={{ background: T.chipBg, border: `1px solid ${T.chipBorder}`, color: T.chipText }}>
            Change projects
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="launcher-user-menu" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <Avatar className="w-9 h-9 border border-white/60 shadow-sm">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="text-xs font-bold bg-[#0B1F3A] text-white">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs opacity-60">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              {user?.platformRole === 'SUPERADMIN' && (
                <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/admin')}>
                  <Shield className="w-3.5 h-3.5 mr-2" /> Admin Portal
                </DropdownMenuItem>
              )}
              {!isTrial && (
                <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings')}>
                  <SettingsIcon className="w-3.5 h-3.5 mr-2" /> Settings
                </DropdownMenuItem>
              )}
              <DropdownMenuItem className="cursor-pointer text-red-500" data-testid="launcher-logout" onClick={handleLogout}>
                <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Hero */}
      <div className="relative z-10 px-8 md:px-14 pt-2 pb-8 max-w-[1400px] mx-auto">
        <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
          className="text-[11px] tracking-[0.28em] uppercase font-semibold"
          style={{ color: '#F26B1F' }}>
          Command Center · {selectedProject?.name || 'Portfolio'}
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.05 }}
          className="mt-2 font-bold tracking-tight" style={{ color: T.title, fontSize: 'clamp(28px, 3.6vw, 44px)', lineHeight: 1.05 }}>
          Where would you like to start today, {user?.name?.split(' ')[0] || 'friend'}?
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.4 }}
          className="mt-3 text-sm max-w-2xl" style={{ color: T.body }}>
          Pick a module to jump in — the sidebar will assemble around you. You can always return here
          from the sidebar's <span className="font-semibold">Modules</span> button.
        </motion.p>
      </div>

      {/* Tile grid — grouped */}
      <div className="relative z-10 px-8 md:px-14 pb-24 max-w-[1400px] mx-auto space-y-14">
        {groups.map((group, gi) => (
          <motion.section key={group.id}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + gi * 0.06, duration: 0.4 }}
            data-testid={`launcher-group-${group.id}`}>
            <div className="flex items-baseline justify-between mb-4 pb-2 border-b" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)' }}>
              <div>
                <h2 className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: '#F26B1F' }}>{group.title}</h2>
                <p className="text-sm mt-1" style={{ color: T.body }}>{group.subtitle}</p>
              </div>
              <span className="text-[10px] opacity-50 font-semibold" style={{ color: T.body }}>{group.tiles.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {group.tiles.map((tile, ti) => {
                const tone = TONE_MAP[tile.tone] || TONE_MAP.b;
                const Icon = tile.icon;
                return (
                  <motion.button key={tile.path}
                    data-testid={`launcher-tile-${tile.path.replace(/[/]/g, '-').replace(/^-/, '')}`}
                    onClick={(e) => handleTileClick(tile, e)}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + gi * 0.06 + ti * 0.03, duration: 0.35 }}
                    whileHover={{ y: -4, scale: 1.01 }}
                    whileTap={{ scale: 0.985 }}
                    className="group relative text-left overflow-hidden rounded-2xl p-5 min-h-[132px]"
                    style={{
                      background: tone.grad,
                      border: '1px solid rgba(15,23,42,0.06)',
                      boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.08)',
                    }}
                  >
                    {/* Halo on hover */}
                    <span className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ boxShadow: `0 0 0 1px ${tone.accent}55, 0 18px 40px -12px ${tone.accent}55` }} />
                    <span className="pointer-events-none absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-0 group-hover:opacity-30 transition-opacity duration-500 blur-3xl"
                      style={{ background: tone.accent }} />

                    <div className="relative flex items-start justify-between gap-3">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-[-4deg]"
                        style={{ background: '#FFFFFF', boxShadow: `inset 0 0 0 1px ${tone.accent}22` }}>
                        <Icon className="w-5 h-5" strokeWidth={1.7} style={{ color: tone.accent }} />
                      </div>
                      <motion.span aria-hidden
                        className="text-[10px] font-bold tracking-widest uppercase opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: tone.accent }}>
                        Open →
                      </motion.span>
                    </div>
                    <div className="relative mt-4">
                      <div className="text-[15px] font-semibold" style={{ color: '#0B1F3A' }}>{tile.label}</div>
                      <div className="text-xs mt-1" style={{ color: '#475569' }}>{tile.desc}</div>
                    </div>
                    <span className="absolute bottom-3 left-5 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} />
                      <span className="text-[9px] tracking-[0.25em] uppercase font-semibold opacity-60" style={{ color: '#0B1F3A' }}>Module</span>
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </motion.section>
        ))}
        {groups.length === 0 && (
          <div className="text-center py-24 opacity-60 text-sm" style={{ color: T.body }}>No modules match "{q}".</div>
        )}
      </div>

      {/* Morph overlay — the clicked tile flies to the sidebar (top-left) */}
      <AnimatePresence>
        {flying && (() => {
          const { rect, tone, Icon, label } = flying;
          const t = TONE_MAP[tone] || TONE_MAP.b;
          return (
            <motion.div
              key="fly"
              initial={{ position: 'fixed', left: rect.left, top: rect.top, width: rect.width, height: rect.height, borderRadius: 16 }}
              animate={{ left: 8, top: 74, width: 260 - 16, height: 44, borderRadius: 8, opacity: 0.98 }}
              exit={{ opacity: 0 }}
              transition={{ type: 'spring', stiffness: 280, damping: 30 }}
              style={{ background: t.grad, border: `1px solid ${t.accent}55`, zIndex: 60, boxShadow: `0 24px 60px -12px ${t.accent}44` }}
              data-testid="launcher-morph"
            >
              <div className="flex items-center gap-2 h-full px-3">
                <Icon className="w-4 h-4" style={{ color: t.accent }} />
                <span className="text-xs font-semibold" style={{ color: '#0B1F3A' }}>{label}</span>
              </div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
}
